/**
 * 2026-06-06 — Quick Create form for Players. Local-match scorers
 * don't have time to fill the full 20-field Add Player wizard; this
 * compact form captures only what's needed to start scoring:
 *   - Name (single field, auto-split on last space → first / last)
 *   - Jersey Number
 *   - WhatsApp number (writes to player.mobile + whatsapp_ok=true)
 *   - Batting hand
 *   - Bowling hand (singular — written to bowling_hands=[hand]; backend
 *     syncs the legacy singular bowling_hand on save)
 *   - Role
 *   - Bowling style
 *
 * Submits via the same POST /api/coredata/players/ endpoint the full
 * AddTemplate uses, so the created player is indistinguishable from one
 * created via the wizard — all the omitted fields land as null/defaults
 * and can be edited later from the player profile.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPlayer } from "../../api/coredata.api";
import {
  BATTING_HANDS,
  BOWLING_HANDS,
  BOWLING_STYLES,
  PLAYER_ROLES,
} from "../../data/lookups";

function splitName(full) {
  const tokens = (full || "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { first_name: "", last_name: "" };
  if (tokens.length === 1) return { first_name: tokens[0], last_name: "" };
  return {
    first_name: tokens.slice(0, -1).join(" "),
    last_name: tokens[tokens.length - 1],
  };
}

export default function QuickPlayerForm({ onSwitchToDetailed }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [jerseyNumber, setJerseyNumber] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [battingHand, setBattingHand] = useState("");
  const [bowlingHand, setBowlingHand] = useState("");
  const [role, setRole] = useState("");
  const [bowlingStyle, setBowlingStyle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError("");
    setBusy(true);

    const { first_name, last_name } = splitName(name);
    const payload = {
      first_name,
      last_name,
      // Display name defaults to the full input so the operator sees
      // exactly what they typed on the scorecard.
      nick_name: name.trim(),
      jersey_number: jerseyNumber.trim() || null,
    };
    const cleanWa = whatsapp.replace(/\s+/g, "");
    if (cleanWa) {
      payload.mobile = cleanWa;
      payload.whatsapp_ok = true;
    }
    if (battingHand) payload.batting_hand = battingHand;
    if (bowlingHand) payload.bowling_hands = [bowlingHand];
    if (role) payload.role = role;
    if (bowlingStyle) payload.bowling_style = bowlingStyle;

    try {
      await createPlayer(payload);
      navigate("/cricket/players");
    } catch (err) {
      const detail =
        err?.response?.data?.detail
        || (typeof err?.response?.data === "string" ? err.response.data : null)
        || err?.message
        || "Couldn't create player.";
      setError(detail);
      setBusy(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Quick Add Player</h1>
          <p className="text-xs text-text-secondary mt-1">
            For local matches — just the essentials. Switch to detailed mode for the full form.
          </p>
        </div>
        {onSwitchToDetailed && (
          <button
            type="button"
            onClick={onSwitchToDetailed}
            className="text-xs text-text-secondary hover:text-text-primary underline whitespace-nowrap"
          >
            Detailed mode
          </button>
        )}
      </div>

      <form
        onSubmit={submit}
        className="bg-bg-primary border border-border-default rounded-2xl p-4 sm:p-6 space-y-4"
      >
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">
            Player name <span className="text-rose-400">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Rohit Sharma"
            autoFocus
            className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
          />
          <p className="text-[10px] text-text-secondary mt-1">
            Last word becomes the surname.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Jersey number
            </label>
            <input
              type="text"
              value={jerseyNumber}
              onChange={(e) => setJerseyNumber(e.target.value)}
              placeholder="e.g. 7"
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              WhatsApp number
            </label>
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="10-digit mobile"
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Batting hand
            </label>
            <select
              value={battingHand}
              onChange={(e) => setBattingHand(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            >
              <option value="">—</option>
              {BATTING_HANDS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Bowling hand
            </label>
            <select
              value={bowlingHand}
              onChange={(e) => setBowlingHand(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            >
              <option value="">—</option>
              {BOWLING_HANDS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            >
              <option value="">—</option>
              {PLAYER_ROLES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Bowling style
            </label>
            <select
              value={bowlingStyle}
              onChange={(e) => setBowlingStyle(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary border border-border-default rounded px-3 py-2 text-sm focus:border-button-primary outline-none"
            >
              <option value="">—</option>
              {BOWLING_STYLES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="bg-button-primary hover:brightness-110 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-semibold"
          >
            {busy ? "Creating…" : "Create player"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/cricket/players")}
            className="text-text-secondary hover:text-text-primary text-sm px-3 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
